export class HeaderBanner {
	static MIN_VISIBLE_MS = 2000;
	static ROTATION_INTERVAL_MS = 5000;
	// Must match the CSS transition duration on .hp-banner__text (see _header.scss).
	static FADE_MS = 300;

	constructor(root) {
		this.banner = root.querySelector('.hp-banner');
		this.bannerText = root.querySelector('.hp-banner__text');
		this.rotationDataEl = root.querySelector('.hp-banner__rotation-data');
		this.header = root.querySelector('.hp-header');
		// A single fixed deadline from mount, not "time since last shown" — otherwise every
		// scroll-up re-show() resets the clock and the next hide() has to wait a further 2s,
		// making the banner feel sluggish long after the initial reveal window has passed.
		this.mountedAt = Date.now();
		this.lastScrollY = window.scrollY;
		this.ticking = false;
		this.rotationIndex = 0;
		this.rotationBanners = null;
		this.rotationStarted = false;
	}

	init() {
		if (!this.banner) return;

		// Guaranteed first reveal: shows on its own after 2s even without any scrolling.
		setTimeout(() => this.show(), HeaderBanner.MIN_VISIBLE_MS);

		window.addEventListener('scroll', () => {
			if (!this.ticking) {
				window.requestAnimationFrame(() => this.handleScroll());
				this.ticking = true;
			}
		});

		this.parseRotationData();
	}

	parseRotationData() {
		if (!this.rotationDataEl || !this.bannerText) return;

		let banners;
		try {
			banners = JSON.parse(this.rotationDataEl.textContent);
		} catch (e) {
			return;
		}
		// Only rendered server-side when there are 2+ overlapping active banners, but
		// guard here too in case that ever changes.
		if (!Array.isArray(banners) || banners.length < 2) return;

		this.rotationBanners = banners;
	}

	// Only ever starts the interval once — called from show(), so the rotation's clock
	// begins counting from the moment the banner is actually first seen, rather than
	// ticking away in the background while it's still hidden and possibly cycling past
	// the first configured banner before anyone ever gets to see it.
	startRotationIfNeeded() {
		if (this.rotationStarted || !this.rotationBanners) return;
		this.rotationStarted = true;

		setInterval(() => this.rotateBanner(), HeaderBanner.ROTATION_INTERVAL_MS);
	}

	rotateBanner() {
		const banners = this.rotationBanners;
		this.rotationIndex = (this.rotationIndex + 1) % banners.length;
		const next = banners[this.rotationIndex];

		this.bannerText.style.opacity = '0';
		setTimeout(() => {
			this.banner.style.background = next.bg;
			this.bannerText.style.color = next.color;
			this.bannerText.innerHTML = next.html;

			// Different banners can wrap to different heights — if the banner is currently
			// shown, re-measure it immediately so .hp-header stays pushed down by exactly
			// the right amount instead of overlapping the new (possibly taller) content.
			if (this.header && this.banner.classList.contains('is-visible')) {
				this.header.style.top = `${this.banner.offsetHeight}px`;
			}

			this.bannerText.style.opacity = '1';
		}, HeaderBanner.FADE_MS);
	}

	show() {
		this.banner.classList.add('is-visible');
		// The banner is position:absolute (out of flow) so .hp-header no longer sits below
		// it automatically — push the header down by the banner's real rendered height,
		// measured fresh each time since text wrapping (and thus height) depends on viewport width.
		if (this.header) {
			this.header.style.top = `${this.banner.offsetHeight}px`;
		}
		this.startRotationIfNeeded();
	}

	hide() {
		// Only the very first reveal is protected — once that initial window has passed,
		// scrolling down hides it immediately, no matter how recently it was last shown.
		if (Date.now() - this.mountedAt < HeaderBanner.MIN_VISIBLE_MS) {
			return;
		}
		this.banner.classList.remove('is-visible');
		if (this.header) {
			this.header.style.top = '0';
		}
	}

	handleScroll() {
		const currentScrollY = window.scrollY;
		const scrollingDown = currentScrollY > this.lastScrollY;
		const scrollingUp = currentScrollY < this.lastScrollY;
		this.lastScrollY = currentScrollY;

		if (scrollingUp) {
			this.show();
		} else if (scrollingDown) {
			if (Date.now() - this.mountedAt < HeaderBanner.MIN_VISIBLE_MS) {
				// Still within the guaranteed first-reveal window — an early scroll-down
				// surfaces the banner early instead of waiting out the rest of the 2s.
				this.show();
			} else {
				// Past that window, only ever hide() here — calling show() on every single
				// scroll-down tick (even when already hidden) kept re-adding then instantly
				// removing 'is-visible' each frame, restarting the banner's own transition
				// on every tick while .hp-header's target never changed, so the header
				// finished moving while the banner kept restarting from scratch.
				this.hide();
			}
		}

		this.ticking = false;
	}
}
